import React, { useState, useEffect, useRef } from "react";
import logo from "../Assets/AWLeaf.png";

const links = [
  { label: "Work", href: "#work" },
  { label: "About", href: "#about" },
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("");

  // A clicked link stays highlighted until the visitor scrolls on their own, even if the
  // page can't scroll that section to the top (e.g. Skills, near the bottom).
  const pinned = useRef(null);

  // One scroll handler drives both the solid bar and the active link, so they can't disagree.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 8);
      if (pinned.current) {
        setActive(pinned.current);
        return;
      }
      const doc = document.documentElement;
      const atBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 2;
      if (atBottom) {
        setActive(links[links.length - 1].href);
        return;
      }
      // Active = the last section whose top has passed the middle of the viewport; none while in the hero.
      const line = window.innerHeight * 0.5;
      let current = "";
      links.forEach(({ href }) => {
        const el = document.querySelector(href);
        if (el && el.getBoundingClientRect().top <= line) current = href;
      });
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const unpin = () => {
      pinned.current = null;
    };
    const userScrollEvents = ["wheel", "touchstart", "keydown"];
    // Any in-page link (nav, mobile menu, logo, hero buttons, "Back to top") pins its target.
    // Links to the hero clear the highlight.
    const onAnchorClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute("href");
      const isSection = links.some((l) => l.href === href);
      pinned.current = isSection ? href : null;
      setActive(isSection ? href : "");
    };
    update();
    document.addEventListener("click", onAnchorClick);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    userScrollEvents.forEach((e) => window.addEventListener(e, unpin, { passive: true }));
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("click", onAnchorClick);
      userScrollEvents.forEach((e) => window.removeEventListener(e, unpin));
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const solid = scrolled || menuOpen;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 px-6 transition-colors duration-200 ${
        solid ? "bg-ink/95 backdrop-blur-md border-b border-line" : "border-b border-transparent"
      }`}
    >
      <nav className="max-w-5xl mx-auto h-16 flex items-center justify-between">
        <a href="#top" aria-label="Andreas Wallin, back to top" className="block">
          <img src={logo} alt="" className="h-5 md:h-6 w-auto" />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              aria-current={active === link.href ? "true" : undefined}
              className={`text-sm transition-colors underline-offset-[8px] decoration-2 decoration-leaf ${
                active === link.href ? "text-fg underline" : "text-dim hover:text-fg"
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <button
          type="button"
          className="md:hidden -mr-2 w-11 h-11 flex items-center justify-center text-dim hover:text-fg"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {menuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M4 7h16M4 12h16M4 17h16"
              />
            )}
          </svg>
        </button>
      </nav>

      {menuOpen && (
        <div id="mobile-menu" className="md:hidden max-w-5xl mx-auto pb-4">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="block py-3 text-base text-dim hover:text-fg border-t border-line first:border-t-0"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}

export default Navbar;
